/**
 * Profile Tab Content — alle 6 Tabs als benannte Komponenten.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Switch } from '../../components/ui/switch';
import {
  User, Mail, Phone, MapPin, Building2, Calendar, Briefcase,
  FileText, GraduationCap, Clock, AlertCircle, Edit3, History,
  ChevronRight, Trash2, Bell, BellRing, UserPlus, KeyRound, Eye, EyeOff, Copy,
  CalendarDays, Pencil, Save, X, ExternalLink,
  Plus, Hourglass, CheckCircle2, XCircle, FileSignature,
  Monitor, Smartphone, LogOut, ShieldCheck, AlarmClock, Coffee,
} from 'lucide-react';
import PushNotificationToggle from '../../components/PushNotificationToggle';
import { AppBadgeDiagnostics } from '../../components/AppBadgeDiagnostics';
import { InfoRow, StatusBadge, ExpiryBadge, formatDate, formatDateTime } from './profileHelpers';
import { myProfileAPI, qualificationProposalsAPI, authAPI } from '../../lib/api';
import { DepartmentBadges } from '../../components/DepartmentBadges';
import { openAuthFile } from '../../lib/openAuthFile';
import { openLoginAndCopy } from '../../lib/itAccessHelpers';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Favicon } from '../../components/Favicon';
import { usePasswordReveal } from '../../hooks/usePasswordReveal';
import { RevealCountdownIcon } from '../../components/RevealCountdownIcon';
import { ProposalDialog } from './ProposalDialog';
import { CONTRACT_TYPE_LABELS } from '../employee/ContractsTab';

const ProfileDepartmentBadges = ({ text }) => (
  <DepartmentBadges
    text={text}
    size="md"
    testIdPrefix="profile-dept"
  />
);

export const PersonalDataTab = ({ employee, openChangeDialog }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg"><User className="h-5 w-5" />Persönliche Informationen</CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        <InfoRow icon={User} label="Vorname" value={employee.first_name} />
        <InfoRow icon={User} label="Nachname" value={employee.last_name} />
        <InfoRow icon={Calendar} label="Geburtsdatum" value={formatDate(employee.date_of_birth)} />
        <InfoRow icon={MapPin} label="Geburtsort" value={employee.place_of_birth} />
        <InfoRow icon={User} label="Geschlecht" value={employee.gender} />
        <InfoRow icon={FileText} label="Staatsangehörigkeit" value={employee.nationality} />
      </CardContent>
    </Card>
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Phone className="h-5 w-5" />
          Private Kontaktdaten
          <Button variant="outline" size="sm" className="ml-auto" onClick={() => openChangeDialog('contact', 'phone')} data-testid="edit-contact-btn">
            <Edit3 className="h-4 w-4 mr-1" />Änderung beantragen
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        <InfoRow icon={Mail} label="E-Mail (privat)" value={employee.email} editable onEdit={() => openChangeDialog('contact', 'email')} />
        <InfoRow icon={Phone} label="Telefon (privat)" value={employee.phone} editable onEdit={() => openChangeDialog('contact', 'phone')} />
        <InfoRow icon={Phone} label="Mobil (privat)" value={employee.mobile} editable onEdit={() => openChangeDialog('contact', 'mobile')} />
      </CardContent>
    </Card>
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg"><MapPin className="h-5 w-5" />Adresse</CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        <InfoRow icon={MapPin} label="Straße" value={employee.street} editable onEdit={() => openChangeDialog('contact', 'street')} />
        <InfoRow icon={MapPin} label="Hausnummer" value={employee.house_number} editable onEdit={() => openChangeDialog('contact', 'house_number')} />
        <InfoRow icon={MapPin} label="PLZ" value={employee.postal_code} editable onEdit={() => openChangeDialog('contact', 'postal_code')} />
        <InfoRow icon={MapPin} label="Stadt" value={employee.city} editable onEdit={() => openChangeDialog('contact', 'city')} />
        <InfoRow icon={MapPin} label="Land" value={employee.country} editable onEdit={() => openChangeDialog('contact', 'country')} />
      </CardContent>
    </Card>
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <AlertCircle className="h-5 w-5" />
          Notfallkontakt
          <Button variant="outline" size="sm" className="ml-auto" onClick={() => openChangeDialog('emergency', 'emergency_contact.name')} data-testid="edit-emergency-btn">
            <Edit3 className="h-4 w-4 mr-1" />Änderung beantragen
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        <InfoRow icon={User} label="Name" value={employee.emergency_contact?.name} editable onEdit={() => openChangeDialog('emergency', 'emergency_contact.name')} />
        <InfoRow icon={Phone} label="Telefon" value={employee.emergency_contact?.phone} editable onEdit={() => openChangeDialog('emergency', 'emergency_contact.phone')} />
        <InfoRow icon={User} label="Beziehung" value={employee.emergency_contact?.relationship} editable onEdit={() => openChangeDialog('emergency', 'emergency_contact.relationship')} />
      </CardContent>
    </Card>
  </div>
);

export const EmploymentDataTab = ({ employee, openChangeDialog }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg"><Briefcase className="h-5 w-5" />Beschäftigungsdaten</CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        <InfoRow icon={Calendar} label="Eintrittsdatum" value={formatDate(employee.employment_start)} />
        {employee.employment_end && (
          <InfoRow icon={Calendar} label="Austrittsdatum" value={formatDate(employee.employment_end)} />
        )}
        <InfoRow icon={Briefcase} label="Einsatz als" value={employee.position} />
        <InfoRow
          icon={Building2}
          label="Maßnahme/Abteilung"
          value={
            employee.department
              ? <ProfileDepartmentBadges text={employee.department} />
              : '-'
          }
        />
        <InfoRow icon={Building2} label="Standort" value={employee.location_name} />
        {employee.additional_location_names && employee.additional_location_names.length > 0 && (
          <InfoRow
            icon={Building2}
            label="Weitere Standorte"
            value={
              <div className="flex flex-wrap gap-1.5">
                {employee.additional_location_names.map((n) => (
                  <Badge key={n} variant="secondary" className="text-xs">{n}</Badge>
                ))}
              </div>
            }
          />
        )}
        <InfoRow icon={Clock} label="Wochenstunden" value={employee.weekly_hours ? `${employee.weekly_hours} Std.` : '-'} />
        <InfoRow icon={Calendar} label="Urlaubstage/Jahr" value={employee.vacation_days_per_year ? `${employee.vacation_days_per_year} Tage` : '-'} />
      </CardContent>
    </Card>
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Building2 className="h-5 w-5" />
          Geschäftliche Kontaktdaten
          <Button variant="outline" size="sm" className="ml-auto" onClick={() => openChangeDialog('contact', 'business_email')} data-testid="edit-business-contact-btn">
            <Edit3 className="h-4 w-4 mr-1" />Änderung beantragen
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        <InfoRow icon={Mail} label="E-Mail (geschäftlich)" value={employee.business_email} editable onEdit={() => openChangeDialog('contact', 'business_email')} />
        <InfoRow icon={Phone} label="Telefon (geschäftlich)" value={employee.business_phone} editable onEdit={() => openChangeDialog('contact', 'business_phone')} />
        <InfoRow icon={Phone} label="Mobil (geschäftlich)" value={employee.business_mobile} editable onEdit={() => openChangeDialog('contact', 'business_mobile')} />
      </CardContent>
    </Card>
  </div>
);

// ContactTab wurde in PersonalDataTab integriert. Wir behalten den Export
// fuer Rueckwaertskompatibilitaet als reinen Re-Export.
export const ContactTab = PersonalDataTab;

const FileLinks = ({ url, filename, testIdPrefix }) => {
  if (!url) {
    return <span className="text-xs text-muted-foreground">Kein Nachweis hinterlegt</span>;
  }
  return (
    <div className="flex flex-wrap items-center gap-3 mt-2">
      <button
        type="button"
        onClick={() =>
          openAuthFile(url, { filename })
            .catch((err) => toast.error(`Anzeigen fehlgeschlagen: ${err.message || err}`))
        }
        className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
        data-testid={`${testIdPrefix}-open`}
      >
        <ExternalLink className="h-3 w-3" />
        {filename || 'Nachweis anzeigen'}
      </button>
      <button
        type="button"
        onClick={() =>
          openAuthFile(url, { filename: filename || 'nachweis.pdf', forceDownload: true })
            .catch((err) => toast.error(`Download fehlgeschlagen: ${err.message || err}`))
        }
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary hover:underline"
        data-testid={`${testIdPrefix}-download`}
      >
        Herunterladen
      </button>
    </div>
  );
};

export const QualificationsTab = ({ employee }) => {
  const [proposals, setProposals] = useState([]);
  const [dialog, setDialog] = useState({ open: false, kind: 'training', action: 'create', initial: {} });

  const reload = async () => {
    try {
      const res = await qualificationProposalsAPI.listMine();
      setProposals(res.data);
    } catch {
      // stillschweigend ignorieren (kein Mitarbeiterprofil-User sieht den Tab eh nicht)
    }
  };

  useEffect(() => { reload(); }, []);

  const pendingFor = (kind) => proposals.filter(p => p.kind === kind && p.status === 'pending');
  const recentDecided = proposals
    .filter(p => p.status !== 'pending')
    .slice(0, 5);

  const openCreate = (kind) => setDialog({ open: true, kind, action: 'create', initial: {} });
  const openUpdate = (kind, item) => setDialog({ open: true, kind, action: 'update', initial: item });

  const requestDelete = async (kind, item) => {
    const label = kind === 'training' ? 'Weiterbildung' : 'Qualifikation';
    const name = item.title || item.name || 'Eintrag';
    if (!window.confirm(`Löschung von „${name}" beantragen? Der Eintrag bleibt sichtbar, bis dein/e Vorgesetzte/r die Löschung freigibt.`)) return;
    try {
      await qualificationProposalsAPI.create({
        kind, action: 'delete', target_id: item.id, payload: {}, reason: '',
      });
      toast.success(`${label}-Löschantrag eingereicht`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Antrag konnte nicht eingereicht werden');
    }
  };

  const withdraw = async (proposal) => {
    if (!window.confirm('Eigenen Antrag wirklich zurückziehen?')) return;
    try {
      await qualificationProposalsAPI.withdraw(proposal.id);
      toast.success('Antrag zurückgezogen');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Zurückziehen fehlgeschlagen');
    }
  };

  const ProposalBadge = ({ proposal }) => {
    if (proposal.status === 'pending') {
      return (
        <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300 gap-1">
          <Hourglass className="h-3 w-3" /> Wartet auf Freigabe
        </Badge>
      );
    }
    if (proposal.status === 'rejected') {
      return (
        <Badge variant="outline" className="bg-red-50 text-red-800 border-red-300 gap-1">
          <XCircle className="h-3 w-3" /> Abgelehnt
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="bg-green-50 text-green-800 border-green-300 gap-1">
        <CheckCircle2 className="h-3 w-3" /> Freigegeben
      </Badge>
    );
  };

  const renderPending = (kind) => {
    const list = pendingFor(kind);
    if (!list.length) return null;
    return (
      <div className="space-y-2 mb-3">
        {list.map(p => {
          const data = p.payload || {};
          const name = data.name || data.title || (p.action === 'delete' ? 'Löschung' : 'Neuer Eintrag');
          return (
            <div key={p.id} className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 p-3" data-testid={`pending-proposal-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium truncate">{name}</p>
                    <ProposalBadge proposal={p} />
                    <Badge variant="secondary" className="text-xs">{
                      p.action === 'create' ? 'Neuer Eintrag' : p.action === 'update' ? 'Änderung' : 'Löschung'
                    }</Badge>
                  </div>
                  {p.reason && <p className="text-xs text-muted-foreground mt-1">„{p.reason}"</p>}
                  {p.document_filename && (
                    <p className="text-xs text-muted-foreground">📎 {p.document_filename}</p>
                  )}
                </div>
                <Button size="sm" variant="ghost" onClick={() => withdraw(p)} data-testid={`withdraw-proposal-${p.id}`}>
                  Zurückziehen
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Weiterbildungen */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="flex items-center gap-2 text-lg"><FileText className="h-5 w-5" />Weiterbildung</CardTitle>
            <Button size="sm" onClick={() => openCreate('training')} data-testid="add-training-btn">
              <Plus className="h-4 w-4 mr-1" /> Neue hinzufügen
            </Button>
          </CardHeader>
          <CardContent>
            {renderPending('training')}
            {employee.trainings && employee.trainings.length > 0 ? (
              <div className="space-y-3">
                {employee.trainings.map((training, idx) => {
                  const id = training.id || `training-${idx}`;
                  return (
                    <div key={id} className="p-3 bg-secondary/30 rounded-lg group" data-testid={`profile-training-${id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">{training.title || training.name}</p>
                          {training.provider && <p className="text-sm text-muted-foreground">{training.provider}</p>}
                          <div className="flex flex-wrap gap-2 mt-1">
                            {(training.completed_date || training.date) && (
                              <Badge variant="outline" className="text-xs">Abgeschlossen: {formatDate(training.completed_date || training.date)}</Badge>
                            )}
                            {training.expiry_date && (
                              <ExpiryBadge expiry={training.expiry_date} />
                            )}
                          </div>
                          <FileLinks
                            url={training.certificate_url}
                            filename={training.certificate_filename}
                            testIdPrefix={`profile-training-${id}`}
                          />
                        </div>
                        {training.id && (
                          <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openUpdate('training', training)} title="Änderung beantragen">
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => requestDelete('training', training)} title="Löschung beantragen">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-4">Keine Weiterbildungen hinterlegt</p>
            )}
          </CardContent>
        </Card>

        {/* Qualifikationen */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="flex items-center gap-2 text-lg"><GraduationCap className="h-5 w-5" />Qualifikationen</CardTitle>
            <Button size="sm" onClick={() => openCreate('qualification')} data-testid="add-qualification-btn">
              <Plus className="h-4 w-4 mr-1" /> Neue hinzufügen
            </Button>
          </CardHeader>
          <CardContent>
            {renderPending('qualification')}
            {employee.qualifications && employee.qualifications.length > 0 ? (
              <div className="space-y-3">
                {employee.qualifications.map((qual, idx) => {
                  const isObj = typeof qual === 'object' && qual !== null;
                  const name = isObj ? qual.name : qual;
                  const id = isObj ? qual.id : `qual-${idx}`;
                  const date = isObj ? qual.date : null;
                  const expiry = isObj ? qual.expiry_date : null;
                  const docUrl = isObj ? qual.document_url : null;
                  const docFilename = isObj ? qual.document_filename : null;
                  return (
                    <div key={id} className="p-3 bg-secondary/30 rounded-lg group" data-testid={`profile-qual-${id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">{name}</p>
                          <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-muted-foreground mt-1">
                            {isObj && qual.institution && <span>{qual.institution}</span>}
                            {date && <span>Erworben: {formatDate(date)}</span>}
                            {expiry && (
                              <ExpiryBadge expiry={expiry} />
                            )}
                          </div>
                          <FileLinks url={docUrl} filename={docFilename} testIdPrefix={`profile-qual-${id}`} />
                        </div>
                        {isObj && qual.id && (
                          <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openUpdate('qualification', qual)} title="Änderung beantragen">
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => requestDelete('qualification', qual)} title="Löschung beantragen">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-4">Keine Qualifikationen hinterlegt</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Arbeitsverträge & Ergänzungen (Lese-Ansicht) */}
      {employee.contracts?.length > 0 && (
        <Card data-testid="profile-contracts-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileSignature className="h-5 w-5" />
              Arbeitsverträge & Ergänzungen
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {employee.contracts.map((c, idx) => {
              const id = c.id || `contract-${idx}`;
              return (
                <div key={id} className="p-3 bg-secondary/30 rounded-lg" data-testid={`profile-contract-${id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-xs">{CONTRACT_TYPE_LABELS[c.contract_type] || 'Vertrag'}</Badge>
                    <p className="font-medium">{c.title}</p>
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-muted-foreground mt-1 items-center">
                    {c.valid_from && <span>Gültig ab: {formatDate(c.valid_from)}</span>}
                    {c.valid_until && <ExpiryBadge expiry={c.valid_until} prefix="Befristet bis" />}
                  </div>
                  {c.notes && <p className="text-sm text-muted-foreground mt-1 italic">„{c.notes}"</p>}
                  <FileLinks url={c.document_url} filename={c.document_filename} testIdPrefix={`profile-contract-${id}`} />
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}


      {/* Historie der letzten Entscheidungen */}
      {recentDecided.length > 0 && (
        <Card className="mt-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><History className="h-4 w-4" />Bearbeitete Anträge</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {recentDecided.map(p => {
              const data = p.payload || {};
              const name = data.name || data.title || 'Eintrag';
              return (
                <div key={p.id} className="flex items-start justify-between gap-2 text-sm border-l-2 border-border pl-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{name}</span>
                      <ProposalBadge proposal={p} />
                    </div>
                    {p.rejection_reason && <p className="text-xs text-muted-foreground italic mt-1">Grund: {p.rejection_reason}</p>}
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{formatDate(p.processed_at)}</span>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      <ProposalDialog
        open={dialog.open}
        onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}
        kind={dialog.kind}
        action={dialog.action}
        initial={dialog.initial}
        onSuccess={reload}
      />
    </>
  );
};

export const RequestsTab = ({ myRequests, loadingRequests, openHistoryDialog, handleDeleteRequest }) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2"><History className="h-5 w-5" />Meine Änderungsanträge</CardTitle>
    </CardHeader>
    <CardContent>
      {loadingRequests ? (
        <div className="space-y-3"><Skeleton className="h-16" /><Skeleton className="h-16" /></div>
      ) : myRequests.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>Keine Änderungsanträge vorhanden</p>
          <p className="text-sm mt-2">
            Nutzen Sie die Bearbeiten-Buttons bei Kontaktdaten oder Notfallkontakt, um Änderungen zu beantragen.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {myRequests.map((request) => (
            <div
              key={request.id}
              className="p-4 border rounded-lg hover:bg-secondary/20 transition-colors"
              data-testid={`request-${request.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <StatusBadge status={request.status} />
                    <span className="text-sm text-muted-foreground">{formatDateTime(request.created_at)}</span>
                  </div>
                  <p className="font-medium">{request.field_label}</p>
                  <div className="text-sm text-muted-foreground mt-1">
                    <span className="line-through">{request.current_value || '(leer)'}</span>
                    <ChevronRight className="h-4 w-4 inline mx-1" />
                    <span className="text-foreground font-medium">{request.new_value}</span>
                  </div>
                  {request.reason && <p className="text-sm text-muted-foreground mt-2">Begründung: {request.reason}</p>}
                  {request.rejection_reason && (
                    <p className="text-sm text-red-500 mt-2">Ablehnungsgrund: {request.rejection_reason}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => openHistoryDialog(request)} data-testid={`history-${request.id}`}>
                    <History className="h-4 w-4" />
                  </Button>
                  {request.status === 'pending' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-600"
                      onClick={() => handleDeleteRequest(request.id)}
                      data-testid={`delete-${request.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

const TestNotificationButton = () => {
  const sendTest = async () => {
    if (!('Notification' in window)) {
      toast.error('Dieser Browser unterstützt keine Desktop-Benachrichtigungen.');
      return;
    }
    let perm = Notification.permission;
    if (perm === 'default') {
      try { perm = await Notification.requestPermission(); } catch { /* ignore */ }
    }
    if (perm === 'denied') {
      toast.error('Benachrichtigungen sind blockiert. Bitte erlaube sie in den Browser-Einstellungen für diese Seite und versuche es erneut.');
      return;
    }
    toast.success('Test-Benachrichtigung gesendet', {
      description: 'Wenn alles funktioniert, erscheint zusätzlich eine Desktop-Benachrichtigung.',
    });
    try {
      // eslint-disable-next-line no-new
      new Notification('TeamHub — Test', {
        body: 'Deine Benachrichtigungen funktionieren. Du wirst künftig ans Ein-/Ausstempeln erinnert.',
      });
    } catch { /* ignore */ }
  };
  return (
    <div className="mt-3">
      <Button
        variant="outline"
        size="sm"
        className="gap-2"
        onClick={sendTest}
        data-testid="send-test-notification-btn"
      >
        <BellRing className="h-4 w-4" />
        Test-Benachrichtigung jetzt senden
      </Button>
      <p className="text-xs text-muted-foreground mt-2">
        Prüfe mit einem Klick, ob Browser-Benachrichtigungen bei dir erlaubt sind und korrekt erscheinen.
      </p>
    </div>
  );
};

const NotificationRow = ({ icon: Icon, color, title, desc, checked, onChange, disabled, testid }) => (
  <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
    <div className="flex items-center gap-3">
      <div className={`p-2 ${color} rounded-lg`}><Icon className="h-5 w-5" /></div>
      <div>
        <p className="font-medium">{title}</p>
        <p className="text-sm text-muted-foreground">{desc}</p>
      </div>
    </div>
    <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={testid} />
  </div>
);

export const ActiveSessionsCard = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await authAPI.getSessions();
      setSessions(data.sessions || []);
    } catch {
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const revoke = async (id, isCurrent) => {
    setBusy(true);
    try {
      await authAPI.revokeSession(id);
      toast.success(isCurrent ? 'Auf diesem Gerät abgemeldet' : 'Gerät abgemeldet');
      if (isCurrent) { window.location.href = '/login'; return; }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Abmelden fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const revokeOthers = async () => {
    setBusy(true);
    try {
      const { data } = await authAPI.revokeOtherSessions();
      toast.success(data.revoked ? `${data.revoked} anderes Gerät/andere Geräte abgemeldet` : 'Keine anderen Geräte aktiv');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const isMobile = (label) => /iPhone|iPad|Android/i.test(label || '');

  return (
    <Card data-testid="active-sessions-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5" />Aktive Anmeldungen</CardTitle>
        <CardDescription>Geräte, auf denen du aktuell angemeldet bist. Melde unbekannte Geräte ab.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="space-y-2"><Skeleton className="h-14" /><Skeleton className="h-14" /></div>
        ) : sessions.length === 0 ? (
          <p className="text-sm text-muted-foreground">Keine aktiven Sitzungen gefunden.</p>
        ) : (
          <>
            {sessions.map((s) => (
              <div key={s.id} data-testid={`session-${s.id}`} className="flex items-center justify-between gap-3 rounded-lg border p-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="shrink-0 rounded-full bg-muted p-2">
                    {isMobile(s.device_label) ? <Smartphone className="h-4 w-4" /> : <Monitor className="h-4 w-4" />}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm truncate">{s.device_label}</span>
                      {s.current && <Badge variant="secondary" data-testid="session-current-badge">Dieses Gerät</Badge>}
                      {s.login_method === 'passkey' && <Badge variant="outline">Passkey</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {s.ip ? `${s.ip} · ` : ''}zuletzt aktiv {s.last_active_label}
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost" size="sm"
                  className="text-red-600 hover:text-red-700 shrink-0"
                  disabled={busy}
                  onClick={() => revoke(s.id, s.current)}
                  data-testid={`session-revoke-${s.id}`}
                >
                  <LogOut className="h-4 w-4 mr-1" />{s.current ? 'Abmelden' : 'Entfernen'}
                </Button>
              </div>
            ))}
            {sessions.some((s) => !s.current) && (
              <Button
                variant="outline" className="w-full" disabled={busy}
                onClick={revokeOthers} data-testid="revoke-other-sessions-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />Auf allen anderen Geräten abmelden
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};


const ReminderToggleRow = ({ icon: Icon, color, title, desc, checked, onChange, disabled, unavailable, testid, children }) => (
  <div className="py-2.5">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${color}`}><Icon className="h-4 w-4" /></div>
        <div>
          <p className="text-sm font-medium">{title}</p>
          <p className="text-xs text-muted-foreground">
            {unavailable ? 'Firmenweit deaktiviert – aktuell nicht verfügbar.' : desc}
          </p>
        </div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled || unavailable} data-testid={testid} />
    </div>
    {checked && !unavailable && !disabled && children}
  </div>
);

export const MyReminderPreferences = () => {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noProfile, setNoProfile] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preMin, setPreMin] = useState('');
  const [endMin, setEndMin] = useState('');

  useEffect(() => {
    let active = true;
    myProfileAPI.getReminderPreferences()
      .then((r) => {
        if (!active) return;
        setPrefs(r.data);
        setPreMin(r.data.pre_shift_reminder_minutes ?? '');
        setEndMin(r.data.end_shift_reminder_minutes ?? '');
      })
      .catch((e) => {
        if (!active) return;
        if (e?.response?.status === 404) setNoProfile(true);
        setPrefs(null);
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const patch = async (payload, revert) => {
    setSaving(true);
    try {
      await myProfileAPI.updateReminderPreferences(payload);
    } catch (e) {
      toast.error('Speichern fehlgeschlagen');
      revert?.();
    } finally {
      setSaving(false);
    }
  };

  const update = (field, value) => {
    const prev = prefs[field];
    setPrefs((p) => ({ ...p, [field]: value }));
    patch({ [field]: value }, () => setPrefs((p) => ({ ...p, [field]: prev })));
  };

  const saveMinutes = (field, raw, setLocal) => {
    const val = raw === '' ? null : Math.max(0, Math.min(180, parseInt(raw, 10) || 0));
    setLocal(val ?? '');
    setPrefs((p) => ({ ...p, [field]: val }));
    patch({ [field]: val });
  };

  if (loading) return <Skeleton className="h-40" />;
  // Kein verknüpftes Mitarbeiterprofil → dezenten Hinweis zeigen statt still zu verschwinden.
  if (noProfile) return (
    <Card data-testid="my-reminders-card-noprofile">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><AlarmClock className="h-5 w-5" />Meine Stempel-Erinnerungen</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          Dein Benutzerkonto ist noch keinem Mitarbeiterprofil zugeordnet. Bitte wende dich an einen Administrator, damit die Stempel-Erinnerungen aktiviert werden können.
        </p>
      </CardContent>
    </Card>
  );
  // Unerwarteter Ladefehler → still ausblenden.
  if (!prefs) return null;

  const masterOff = prefs.reminder_enabled === false;

  const minutesInput = (value, setLocal, field, companyDefault, testid) => (
    <div className="mt-2 ml-11">
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min={0}
          max={180}
          value={value}
          placeholder={String(companyDefault)}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={(e) => saveMinutes(field, e.target.value, setLocal)}
          className="h-8 w-20 text-sm"
          data-testid={testid}
        />
        <span className="text-xs text-muted-foreground">Min vorher</span>
      </div>
      <p className="text-[11px] text-muted-foreground mt-1">
        Leer = Firmen-Standard ({companyDefault} Min)
      </p>
    </div>
  );

  return (
    <Card data-testid="my-reminders-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><AlarmClock className="h-5 w-5" />Meine Stempel-Erinnerungen</CardTitle>
        <CardDescription>
          Steuere selbst, welche automatischen Erinnerungen du erhältst und wie früh. Erfordert aktivierte Push-Benachrichtigungen.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ReminderToggleRow
          icon={BellRing} color="bg-blue-100 text-blue-600"
          title="Automatische Erinnerungen (gesamt)"
          desc="Hauptschalter für alle Stempel-Erinnerungen unten."
          checked={prefs.reminder_enabled}
          onChange={(c) => update('reminder_enabled', c)}
          disabled={saving}
          testid="toggle-my-reminder-master"
        />
        <div className="border-t pt-1 mt-1 pl-1">
          <ReminderToggleRow
            icon={AlarmClock} color="bg-emerald-100 text-emerald-600"
            title="Vor Dienstbeginn (Einstempeln)"
            desc="Push kurz vor deinem geplanten Dienstbeginn."
            checked={prefs.pre_shift_reminder_enabled}
            onChange={(c) => update('pre_shift_reminder_enabled', c)}
            disabled={saving || masterOff}
            unavailable={!prefs.company_pre_enabled}
            testid="toggle-my-pre-shift"
          >
            {minutesInput(preMin, setPreMin, 'pre_shift_reminder_minutes', prefs.company_pre_minutes, 'input-my-pre-minutes')}
          </ReminderToggleRow>
          <ReminderToggleRow
            icon={LogOut} color="bg-orange-100 text-orange-600"
            title="Vor Feierabend (Ausstempeln)"
            desc="Push kurz vor deinem geplanten Dienstende."
            checked={prefs.end_shift_reminder_enabled}
            onChange={(c) => update('end_shift_reminder_enabled', c)}
            disabled={saving || masterOff}
            unavailable={!prefs.company_end_enabled}
            testid="toggle-my-end-shift"
          >
            {minutesInput(endMin, setEndMin, 'end_shift_reminder_minutes', prefs.company_end_minutes, 'input-my-end-minutes')}
          </ReminderToggleRow>
          <ReminderToggleRow
            icon={Coffee} color="bg-amber-100 text-amber-600"
            title="Pausen-Erinnerung"
            desc="Push, wenn deine laufende Pause zu lang wird."
            checked={prefs.break_reminder_enabled}
            onChange={(c) => update('break_reminder_enabled', c)}
            disabled={saving || masterOff}
            unavailable={!prefs.company_break_enabled}
            testid="toggle-my-break"
          />
        </div>
        {masterOff && (
          <p className="text-xs text-muted-foreground mt-2">
            Der Hauptschalter ist aus – es werden keine Stempel-Erinnerungen gesendet.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export const NotificationsTab = ({ loadingNotifSettings, notificationSettings, savingNotifSettings, updateNotificationSetting }) => (
  <div className="space-y-6">
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2"><Bell className="h-5 w-5" />Benachrichtigungseinstellungen</CardTitle>
      <CardDescription>Legen Sie fest, welche Benachrichtigungen Sie erhalten möchten.</CardDescription>
    </CardHeader>
    <CardContent>
      {loadingNotifSettings ? (
        <div className="space-y-4">
          <Skeleton className="h-12" /><Skeleton className="h-12" /><Skeleton className="h-12" /><Skeleton className="h-12" />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="p-4 border rounded-lg bg-gradient-to-r from-blue-50 to-purple-50">
            <div className="flex items-center gap-2 mb-3">
              <BellRing className="h-5 w-5 text-blue-600" />
              <h3 className="font-semibold text-blue-900">Push-Benachrichtigungen</h3>
            </div>
            <PushNotificationToggle />
            <TestNotificationButton />
          </div>

          <AppBadgeDiagnostics />

          <MyReminderPreferences />

          <div className="border-t pt-6">
            <h3 className="text-sm font-medium text-muted-foreground mb-4">In-App Benachrichtigungen</h3>
          </div>

          <NotificationRow
            icon={UserPlus} color="bg-blue-100 text-blue-600"
            title="Workflow-Aufgaben"
            desc="Benachrichtigung bei neuen On-/Off-Boarding Aufgaben"
            checked={notificationSettings.workflow_tasks}
            onChange={(c) => updateNotificationSetting('workflow_tasks', c)}
            disabled={savingNotifSettings}
            testid="toggle-workflow-tasks"
          />
          <NotificationRow
            icon={FileText} color="bg-purple-100 text-purple-600"
            title="Änderungsanträge"
            desc="Benachrichtigung bei neuen Anträgen und Entscheidungen"
            checked={notificationSettings.change_requests}
            onChange={(c) => updateNotificationSetting('change_requests', c)}
            disabled={savingNotifSettings}
            testid="toggle-change-requests"
          />
          <NotificationRow
            icon={Calendar} color="bg-amber-100 text-amber-600"
            title="Urlaubsanträge"
            desc="Benachrichtigung bei neuen Anträgen und Genehmigungen"
            checked={notificationSettings.vacation_requests}
            onChange={(c) => updateNotificationSetting('vacation_requests', c)}
            disabled={savingNotifSettings}
            testid="toggle-vacation-requests"
          />
          <NotificationRow
            icon={Clock} color="bg-orange-100 text-orange-600"
            title="Korrekturanträge"
            desc="Benachrichtigung bei neuen Zeitkorrekturen und Entscheidungen"
            checked={notificationSettings.correction_requests}
            onChange={(c) => updateNotificationSetting('correction_requests', c)}
            disabled={savingNotifSettings}
            testid="toggle-correction-requests"
          />
          <NotificationRow
            icon={CalendarDays} color="bg-emerald-100 text-emerald-600"
            title="Einsatzplan-Änderungen"
            desc="Push, wenn dein Einsatz geändert, gestrichen oder neu zugewiesen wird (nicht bei Ersteintrag)"
            checked={notificationSettings.shift_changes}
            onChange={(c) => updateNotificationSetting('shift_changes', c)}
            disabled={savingNotifSettings}
            testid="toggle-shift-changes"
          />
        </div>
      )}
    </CardContent>
  </Card>
  <ActiveSessionsCard />
  </div>
);


export const ITAccessTab = () => {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const reveal = usePasswordReveal({ totalMs: 10000 });
  const [editing, setEditing] = React.useState(null); // active access entry
  const [form, setForm] = React.useState({});
  const [showPwInDialog, setShowPwInDialog] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await myProfileAPI.getItAccess();
        if (!cancelled) setItems(r.data || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggle = (id) => reveal.toggle(id);
  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      login: a.login || '',
      password: a.password || '',
      url: a.url || '',
      info: a.info || '',
    });
    setShowPwInDialog(false);
  };

  const closeEdit = () => {
    setEditing(null);
    setForm({});
    setShowPwInDialog(false);
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {
        login: form.login || null,
        password: form.password || null,
        url: form.url || null,
        info: form.info || null,
      };
      const r = await myProfileAPI.updateItAccess(editing.id, payload);
      setItems(prev => prev.map(it => (it.id === editing.id ? r.data : it)));
      toast.success('IT-Zugang aktualisiert');
      closeEdit();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Skeleton className="h-48" />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <KeyRound className="h-5 w-5" /> Meine IT-Zugänge
        </CardTitle>
        <CardDescription>
          Übersicht über alle Ihnen zugewiesenen Programm- und System-Zugänge.
          Passwörter sind standardmäßig verdeckt.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            <KeyRound className="h-10 w-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Keine IT-Zugänge hinterlegt.</p>
            <p className="text-xs mt-1">Sobald Ihre IT-/Personalabteilung Zugänge einrichtet, erscheinen sie hier.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="my-it-access-list">
            {items.map((a) => (
              <div key={a.id} className="border rounded-lg p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Favicon url={a.url} size={24} />
                    <div className="min-w-0">
                      <p className="font-medium truncate">{a.program}</p>
                      {a.url && (
                        <a href={a.url} target="_blank" rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline truncate block">
                          {a.url}
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {a.status && <Badge variant="outline" className="text-[11px]">{a.status}</Badge>}
                    {a.url && (
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => openLoginAndCopy({ url: a.url, login: a.login })}
                        title={a.login ? 'Login-Seite oeffnen & Login kopieren' : 'Login-Seite oeffnen'}
                        className="h-7 w-7 p-0"
                        data-testid={`open-it-access-${a.id}`}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => openEdit(a)}
                      title="Bearbeiten"
                      className="h-7 w-7 p-0"
                      data-testid={`edit-it-access-${a.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  {a.login && (
                    <div className="flex items-center gap-2 min-w-0 sm:col-span-2">
                      <User className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                      <span className="font-mono text-xs truncate flex-1">{a.login}</span>
                      <Button variant="ghost" size="sm" onClick={() => copyToClipboard(a.login)}
                        title="Login kopieren" className="h-6 w-6 p-0">
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                  {a.password && (
                    <div className="flex items-center gap-2 min-w-0 sm:col-span-2">
                      <KeyRound className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                      <span className="font-mono text-xs truncate flex-1">
                        {reveal.isVisible(a.id) ? a.password : '••••••••••'}
                      </span>
                      <Button variant="ghost" size="sm" onClick={() => toggle(a.id)}
                        title={reveal.isVisible(a.id) ? `Ausblenden (auto in ${Math.ceil(reveal.remainingPct(a.id) / 10)} s)` : 'Anzeigen (10 s)'}
                        className="h-6 w-6 p-0"
                        data-testid={`toggle-pw-${a.id}`}>
                        <RevealCountdownIcon
                          visible={reveal.isVisible(a.id)}
                          remainingPct={reveal.remainingPct(a.id)}
                          size={14}
                        />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => copyToClipboard(a.password)}
                        title="Passwort kopieren" className="h-6 w-6 p-0">
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>

                {a.info && (
                  <p className="text-xs text-muted-foreground border-t pt-2">{a.info}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Edit-Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && closeEdit()}>
        <DialogContent className="max-w-md w-[calc(100vw-1rem)] sm:w-full p-4 sm:p-6 max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5" />
              {editing?.program} bearbeiten
            </DialogTitle>
            <DialogDescription>
              Aktualisiere deine eigenen Zugangsdaten — z.B. wenn du dein Passwort im
              externen System geändert hast. Den Programm-Namen kann nur die IT/HR
              ändern.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="it-edit-login">Login / Benutzername</Label>
              <Input
                id="it-edit-login"
                value={form.login || ''}
                onChange={(e) => setForm(f => ({ ...f, login: e.target.value }))}
                autoComplete="off"
                data-testid="it-edit-login"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="it-edit-pw">Passwort</Label>
              <div className="relative">
                <Input
                  id="it-edit-pw"
                  type={showPwInDialog ? 'text' : 'password'}
                  value={form.password || ''}
                  onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                  autoComplete="new-password"
                  className="pr-10 font-mono"
                  data-testid="it-edit-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwInDialog(s => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  title={showPwInDialog ? 'Ausblenden' : 'Anzeigen'}
                >
                  {showPwInDialog ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Wird verschlüsselt gespeichert und ist nur für dich und die IT/HR sichtbar.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="it-edit-url">Login-URL (optional)</Label>
              <Input
                id="it-edit-url"
                value={form.url || ''}
                onChange={(e) => setForm(f => ({ ...f, url: e.target.value }))}
                placeholder="https://..."
                data-testid="it-edit-url"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="it-edit-info">Notiz (optional)</Label>
              <Textarea
                id="it-edit-info"
                value={form.info || ''}
                onChange={(e) => setForm(f => ({ ...f, info: e.target.value }))}
                rows={3}
                data-testid="it-edit-info"
              />
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={closeEdit} disabled={saving} className="w-full sm:w-auto">
              <X className="h-4 w-4 mr-2" /> Abbrechen
            </Button>
            <Button onClick={saveEdit} disabled={saving} className="w-full sm:w-auto" data-testid="it-edit-save">
              <Save className="h-4 w-4 mr-2" /> {saving ? 'Speichert…' : 'Speichern'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
